import type { FC, ReactNode } from "react";
import { useState } from "react";
import { Button } from "@similarweb/ui-components";
import { Dropdown } from "@similarweb/ui-components";
import {
    PopupContentText,
    DropdownHeader,
    Row,
    RowWrapper,
    TooltipBody,
} from "app/components/React/GAVerifiedIcon/StyledComponents";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { translate } from "app/filters/i18nFilter";
import { swSettings } from "app/common/services/swSettings";
import { GAWizardModal } from "app/components/GA-verify/GAWizardModal";
import { OnOffSwitch } from "@similarweb/ui-components";
import { estimatedVsGaSwitchToggle } from "app/actions/commonActions";
import { estimationsVsGaUserPreferencesUpdate } from "app/actions/estimationsVsGaUserPreferencesUpdate";
import { useDispatch, useSelector } from "react-redux";
import { PlainTooltip } from "@similarweb/ui-components";
import { allTrackers } from "app/services/track/track";
import { useCurrentStateAndParams } from "@uirouter/react";
import { resolveRouterTrackingId } from "single-spa/react-app/router/resolveRouterTrackingId";

const TooltipContent = ({ isConnected }) => {
    const tooltipText = isConnected
        ? "ga4.pop.up.cta.button.connected.tooltip.body"
        : "ga4.pop.up.cta.button.connect.tooltip.body";
    return <TooltipBody>{translate(tooltipText)}</TooltipBody>;
};

interface IGA4VerifiedContainer {
    hasGAToken: boolean;
    isPrivate: boolean;
}

export const GA4VerifiedContainer: FC<IGA4VerifiedContainer> = ({ isPrivate, hasGAToken }) => {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const { showGAApprovedData } = useSelector((state) => state.common);
    const dispatch = useDispatch();
    const { state, params } = useCurrentStateAndParams();
    const isAccountGAConnected = swSettings.components.Home.resources.IsConnectedGoogleAnalyticsAccount;
    const subSubSection = resolveRouterTrackingId(state, params)?.subSubSection;

    const onItemClick = (item) => {
        item.onClick?.();
    };

    const onEditClick = () => {
        allTrackers.dispatchTrackEvent("Edit GA", "click", "Edit", {
            sub_sub_section: subSubSection,
        });
        setIsModalOpen(true);
    };

    const onConnectYourWebsiteClick = () => {
        allTrackers.dispatchTrackEvent("Connect GA", "click", "Connect your website", {
            sub_sub_section: subSubSection,
        });
        setIsModalOpen(true);
    };

    const onToggleShowGAData = (isGASelected: boolean) => {
        dispatch(estimatedVsGaSwitchToggle(!isGASelected));
        dispatch(
            estimationsVsGaUserPreferencesUpdate({
                type: "WebsiteAnalysisVerifiedData",
                isEnabled: !isGASelected,
            }),
        );
        allTrackers.dispatchTrackEvent("Show GA data", isGASelected ? "off" : "on", "Show GA data", {
            clicked_prom: "Pop-up",

            sub_sub_section: subSubSection,
        });
    };

    const getButtonTextKey = (): string => {
        if (isAccountGAConnected && isPrivate) {
            return "ga4.pop.up.cta.button.privately";
        } else if (isPrivate || !hasGAToken) {
            return "ga4.pop.up.cta.button.connect";
        } else {
            return "ga4.pop.up.cta.button.publicly";
        }
    };

    // in case 1.the website not connected to GA 2. the website is private & is not the user own website
    const renderOnlyButton = !hasGAToken || (isPrivate && !isAccountGAConnected);

    const showEditRow = isAccountGAConnected && swSettings.isCurrentDomainRelatedToUser();

    const onGAModalClose = () => {
        setIsModalOpen(false);
    };
    const elements = [
        <Button
            type={"outlined"}
            key={"ga.button"}
        >
            {translate(getButtonTextKey())}
        </Button>,
        <RowWrapper
            key={"ga.data"}
            onClick={() => onToggleShowGAData(showGAApprovedData)}
        >
            <Row>
                <SWReactIcons
                    iconColor={colorsPalettes.carbon[200]}
                    iconName={"checked"}
                    size={"sm"}
                />
                <PopupContentText>{translate("ga4.pop.up.show.data")}</PopupContentText>
            </Row>
            <OnOffSwitch isSelected={showGAApprovedData} />
        </RowWrapper>,
        showEditRow && (
            <RowWrapper
                onClick={onEditClick}
                key={"ga.edit"}
            >
                <Row>
                    <SWReactIcons
                        iconName={"edit-icon"}
                        size={"sm"}
                        iconColor={colorsPalettes.carbon[200]}
                    />
                    <PopupContentText>{translate("ga4.pop.up.open.modal")}</PopupContentText>
                </Row>
            </RowWrapper>
        ),
    ].filter(Boolean);
    const renderConnectComponent = (): ReactNode => {
        return renderOnlyButton ? (
            <PlainTooltip
                tooltipContent={<TooltipContent isConnected={false} />}
                placement={"top"}
            >
                <span>
                    <Button
                        onClick={onConnectYourWebsiteClick}
                        type={"outlined"}
                    >
                        {translate(getButtonTextKey())}
                    </Button>
                </span>
            </PlainTooltip>
        ) : (
            <PlainTooltip
                tooltipContent={<TooltipContent isConnected={true} />}
                placement={"top"}
            >
                <span>
                    <Dropdown
                        width={320}
                        dropdownPopupPlacement={"bottom"}
                        onClick={onItemClick}
                        headerComponent={<DropdownHeader>{translate("ga4.pop.up.title")}</DropdownHeader>}
                    >
                        {elements}
                    </Dropdown>
                </span>
            </PlainTooltip>
        );
    };

    const GAIconName = () => {
        if (showGAApprovedData) {
            return "ga-icon-active";
        } else if (renderOnlyButton) {
            return "ga-icon-disabled";
        } else if (isAccountGAConnected && isPrivate) {
            return "ga-icon-locked";
        } else {
            return "ga-icon";
        }
    };

    return (
        <FlexRow
            gap={"8px"}
            alignItems={"center"}
        >
            <SWReactIcons
                iconName={GAIconName()}
                size={"sm"}
            />
            {renderConnectComponent()}
            <GAWizardModal
                isOpen={isModalOpen}
                onClose={onGAModalClose}
            />
        </FlexRow>
    );
};
